import { useEffect, useMemo, useRef } from "react";
import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useApp } from "@/contexts/AppContext";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";
import {
  breakfastEventsPrefix,
  pendingInvitesPrefix,
} from "@/features/breakfast/queryKeys";
import {
  circleInvitesSentPrefix,
  circleMembersPrefix,
  circlePeerScopesPrefix,
  circlesSchedulePrefix,
} from "@/features/circles/queryKeys";

/**
 * RealtimeSync — Headless component that manages global Postgres subscriptions.
 * Centralizes all data invalidation logic to ensure "Continuous Refresh"
 * across all browser tabs and users with zero redundant refetches.
 *
 * ## Coalescing (audit W4)
 *
 * Previously every postgres_changes event invoked `invalidateQueries`
 * synchronously. A burst of 5 rapid writes → 5 immediate network refetches,
 * and a verbose `console.log` per event cluttered the production console.
 *
 * We now:
 *   1. Collect query keys into a `Set` (dedupe by JSON shape) across a
 *      short debounce window.
 *   2. Flush them together with a single `invalidateQueries` call per key.
 *   3. Gate all diagnostic logs behind `import.meta.env.DEV`.
 */

const INVALIDATE_DEBOUNCE_MS = 300;
const IS_DEV = import.meta.env.DEV;

/**
 * Debounced, coalescing invalidator. Keys are serialized to JSON so the
 * `Set` dedupes multi-identity refs (e.g. two emitters scheduling the same
 * prefix inside the same frame). Flush fires once per window.
 *
 * NOTE on `refetchType: "active"` — this is deliberate. `invalidateQueries`
 * defaults to refetching ALL matching queries, including inactive ones
 * whose observers have unmounted. That's wasteful (and noisy) when the
 * user is nowhere near the data. We only refetch what's actually on
 * screen right now; everything else is simply marked stale and will
 * refetch naturally if/when it remounts.
 */
function createDebouncedInvalidator(client: QueryClient) {
  const pending = new Set<string>();
  const keyMap = new Map<string, QueryKey>();
  let handle: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    handle = null;
    const queued = Array.from(pending);
    pending.clear();
    for (const serialized of queued) {
      const key = keyMap.get(serialized);
      if (!key) continue;
      keyMap.delete(serialized);
      // `refetchType: "active"` → the currently-mounted CircleView /
      // PendingInvitesList / etc. refetches in the background silently,
      // the stealth top-bar indicator lights up for a beat, and the UI
      // transitions from "Awaiting" → accepted state with zero blocking.
      client.invalidateQueries({ queryKey: key, refetchType: "active" });
    }
  };

  const schedule = (key: QueryKey) => {
    const serialized = JSON.stringify(key);
    pending.add(serialized);
    keyMap.set(serialized, key);
    if (handle) return;
    handle = setTimeout(flush, INVALIDATE_DEBOUNCE_MS);
  };

  const cancel = () => {
    if (handle) clearTimeout(handle);
    handle = null;
    pending.clear();
    keyMap.clear();
  };

  return { schedule, cancel };
}

export function RealtimeSync() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useApp();

  // Stable across renders so the Supabase channel callbacks always close
  // over the latest invalidator without re-subscribing on every render.
  const invalidatorRef = useRef<ReturnType<typeof createDebouncedInvalidator> | null>(null);

  // Recreate the invalidator if the QueryClient identity changes (extremely
  // rare, but keeps semantics correct in tests / provider swaps).
  const invalidator = useMemo(() => {
    invalidatorRef.current?.cancel();
    const next = createDebouncedInvalidator(queryClient);
    invalidatorRef.current = next;
    return next;
  }, [queryClient]);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (IS_DEV) console.log("RealtimeSync: Initializing global listeners...");

    const schedule = invalidator.schedule;

    const channel = supabase
      .channel("global-app-sync")
      // 1. Attendance & AI Suggestions
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tracked_days" },
        (payload) => {
          if (IS_DEV) console.log("RealtimeSync: tracked_days changed", payload);
          schedule(trackedDaysPrefix);
          schedule(circlesSchedulePrefix);
          schedule(["workspace-team"]);
        }
      )
      // 2. Social Events (Breakfast/Lunch)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "breakfast_events" },
        () => {
          if (IS_DEV) console.log("RealtimeSync: breakfast_events changed");
          schedule(breakfastEventsPrefix);
          schedule(pendingInvitesPrefix);
        }
      )
      // 3. RSVPs & Potluck Items
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "breakfast_attendees" },
        () => {
          if (IS_DEV) console.log("RealtimeSync: breakfast_attendees changed");
          schedule(breakfastEventsPrefix);
          // Also poke the RSVP Center so a new invite row (INSERT) or a
          // status flip (pending → confirmed/declined) is reflected in the
          // Pending Invites sidebar + bell badge in realtime.
          schedule(pendingInvitesPrefix);
          schedule(circlesSchedulePrefix);
        }
      )
      // 3b. Potluck Items (claimed_by updates, host inserts/deletes).
      // EventDetailDrawer also subscribes per-event for faster local UX, but
      // we listen globally here so the Circles card list stays in sync for
      // users who don't have the drawer open. Double invalidations on the
      // same key are harmless — the debouncer dedupes anyway.
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "potluck_items" },
        () => {
          if (IS_DEV) console.log("RealtimeSync: potluck_items changed");
          schedule(breakfastEventsPrefix);
        }
      )
      // 4. Circle Connections
      //
      // Fires on INSERT (new invite sent), UPDATE (pending → accepted /
      // declined), and DELETE (revoke / remove). All three transitions
      // affect BOTH sides of the connection, so we fan out to every
      // per-user key the Circles UI observes:
      //   • circleMembersPrefix       → accepted-friends list
      //   • circleInvitesSentPrefix   → "Awaiting response" panel
      //   • circlesSchedulePrefix     → circle-wide calendar view
      //   • ["buddy-ids"]             → header avatars / dashboard
      //
      // With `refetchType: "active"` above, whichever of these screens
      // the user is currently looking at will silently refetch and the
      // "Awaiting" → "Accepted" flip happens in place — no manual
      // reload, no full-page spinner, matches the behavior of every
      // professional social product.
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_connections" },
        (payload) => {
          if (IS_DEV) {
            console.log(
              "RealtimeSync: user_connections",
              payload.eventType,
              payload,
            );
          }
          schedule(circleMembersPrefix);
          schedule(circlesSchedulePrefix);
          schedule(circlePeerScopesPrefix);
          schedule(["buddy-ids"]);
          schedule(circleInvitesSentPrefix);
        }
      )
      // 5. System Notifications
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications" },
        () => {
          if (IS_DEV) console.log("RealtimeSync: notifications changed");
          schedule(["notifications"]);
        }
      )
      // 6. User Profile & Preferences
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles" },
        () => {
          if (IS_DEV) console.log("RealtimeSync: profiles changed");
          schedule(["profile"]);
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_settings" },
        () => {
          if (IS_DEV) console.log("RealtimeSync: user_settings changed");
          schedule(["settings"]);
        }
      )
      .subscribe((status, err) => {
        // Surface subscription failures loudly in development so a missing
        // publication membership (e.g. `user_connections` not in
        // `supabase_realtime`) doesn't silently regress to polling-only.
        // In production we stay quiet; React Query's `invalidateQueries`
        // on mutation completion and the `refetchOnReconnect` default
        // still keep the UI correct — just not strictly live.
        if (IS_DEV && status !== "SUBSCRIBED") {
          console.warn("RealtimeSync: channel status", status, err);
        }
      });

    return () => {
      if (IS_DEV) console.log("RealtimeSync: Cleaning up global listeners...");
      invalidator.cancel();
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, invalidator]);

  return null;
}
